import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, orderBy, setDoc, serverTimestamp, onSnapshot, limit } from 'firebase/firestore';
import { allocateTaskNumber } from '../utils/associationUtils';
import { db } from '../utils/firebase';
import TaskPage from './TaskPage';
import Toast from '../components/common/Toast';
import DateRangeSelector from '../components/common/DateRangeSelector';
import TaskModal from '../components/forms/TaskModal';
import UserModal from '../components/forms/UserModal';
import { useAuth } from '../components/auth/AuthContext';
import { calculateWinProbability } from '../utils/winProbability';
import { formatDate } from '../utils/dateHelpers';
import { collectionPath } from '../utils/firestoreCollections';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, canViewAllData } from '../utils/permissions';
import { generateTaskId } from '../utils/documentIdUtils';
import PaginationControls from '../components/common/PaginationControls';
import { logEntityChanges } from '../utils/logger';

const USERS_PAGE_SIZE = 25;
const OPP_PAGE_SIZE = 25;
const TASK_PAGE_SIZE = 25;
const LOG_PAGE_SIZE = 25;

const SalesRepDetailPage = () => {
    const { uid } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { can } = usePermissions(currentUser);
    const [rep, setRep] = useState(null);
    const [logs, setLogs] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [showTaskModal, setShowTaskModal] = useState(false); // Add state for task modal
    const [showEditModal, setShowEditModal] = useState(false);

    // Add state for activity log search and pagination
    const [logSearch, setLogSearch] = useState('');
    const [logPage, setLogPage] = useState(1);
    const [activeTab, setActiveTab] = useState('tasks');
    const [oppSearch, setOppSearch] = useState('');
    const [oppSort, setOppSort] = useState({ column: 'startDate', asc: true });
    const [oppRange, setOppRange] = useState("30");
    const [oppCustomStart, setOppCustomStart] = useState("");
    const [oppCustomEnd, setOppCustomEnd] = useState("");
    const [oppPage, setOppPage] = useState(1);
    const [taskPage, setTaskPage] = useState(1);
    const [taskSearch, setTaskSearch] = useState('');
    const [totalTaskPages, setTotalTaskPages] = useState(1);
    const [totalTaskCount, setTotalTaskCount] = useState(0);
    const [users, setUsers] = useState([]); // State to store fetched users
    const [activitySearch, setActivitySearch] = useState('');

    // Date range calculation for opportunities
    const { start: oppStart, end: oppEnd } = React.useMemo(() => {
        const now = new Date();
        let start, end;
        if (oppRange === "custom" && oppCustomStart && oppCustomEnd) {
            start = new Date(oppCustomStart);
            end = new Date(oppCustomEnd);
        } else if (oppRange === "ytd") {
            start = new Date(now.getFullYear(), 0, 1);
            end = now;
        } else {
            end = now;
            start = new Date(now.getTime() - Number(oppRange) * 24 * 60 * 60 * 1000);
        }
        return { start, end };
    }, [oppRange, oppCustomStart, oppCustomEnd]);

    // Move fetchRepAndcustomers OUTSIDE of useEffect and memoize it
    const fetchRepAndcustomers = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch sales rep info
            const repSnap = await getDoc(doc(db, collectionPath('users'), uid));
            if (!repSnap.exists()) {
                setRep(null);
                setLoading(false);
                return;
            }
            
            const repData = { id: repSnap.id, ...repSnap.data() };
            setRep(repData);

            // Get the display name for this sales rep
            const repDisplayName = repData.displayName || 
                                  `${repData.firstName || ''} ${repData.lastName || ''}`.trim() || 
                                  repData.email || 
                                  '';

            // Fetch opportunities assigned to this sales rep (by uid)
            const oppQ = query(
                collection(db, collectionPath('opportunities')),
                where('uid', '==', uid),
                orderBy('startDate', 'desc'),
                limit(200)
            );
            const oppSnap = await getDocs(oppQ);
            const opps = oppSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setOpportunities(opps);

        } catch (error) {
            console.error('Error fetching rep and opportunities:', error);
            setOpportunities([]);
        } finally {
            setLoading(false);
        }
    }, [uid]); // Add uid as dependency

    useEffect(() => {
        if (uid) {
            fetchRepAndcustomers();
        }
    }, [uid, fetchRepAndcustomers]); // Add fetchRepAndcustomers to dependencies

    // Reset to first page when search changes
    useEffect(() => {
        setLogPage(1);
    }, [logSearch]);
    
    useEffect(() => {
        setTaskPage(1);
    }, [taskSearch]); // Changed from rep dependency

    // Filter logs to only show activity where the userId matches the sales rep's id
    const filteredLogs = logs
        .filter(log => log.entityType === 'User' && log.entityId === uid)
        .filter(log => {
            const dateObj = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
            const dateStr = dateObj.toLocaleDateString().toLowerCase();
            const timeStr = dateObj.toLocaleTimeString().toLowerCase();
            const actionStr = (log.action || '').toLowerCase();
            const detailsStr = (log.details || '').toLowerCase();
            const searchLower = logSearch.toLowerCase();
            return (
                !logSearch ||
                dateStr.includes(searchLower) ||
                timeStr.includes(searchLower) ||
                actionStr.includes(searchLower) ||
                detailsStr.includes(searchLower)
            );
        });

    // --- Logs Pagination ---
    const totalLogPages = useMemo(() => 
        Math.ceil(filteredLogs.length / LOG_PAGE_SIZE),
        [filteredLogs.length]
    );
    
    const paginatedLogs = useMemo(() => 
        filteredLogs.slice((logPage - 1) * LOG_PAGE_SIZE, logPage * LOG_PAGE_SIZE),
        [filteredLogs, logPage]
    );

    // Add opportunity fields for table headers - memoized
    const displayedOpportunityFields = useMemo(() => [
        'opportunityNumber',
        'stage',
        'customer',
        'leadSource',
        'value',
        'startDate',
        'dueDate',
        'salesRep',
        'winProbability'
    ], []);

    // Filter and sort opportunities for the table - optimized with useMemo
    const filteredOpportunities = useMemo(() => {
        return opportunities.filter(opp => {
            // Search filter
            if (oppSearch) {
                const searchLower = oppSearch.toLowerCase();
                const columns = [
                    opp.stage,
                    opp.customerName || opp.customerName,
                    opp.leadSource,
                    opp.value?.toString(),
                    opp.startDate || opp.date,
                    opp.dueDate,
                    rep?.displayName || `${rep?.firstName || ''} ${rep?.lastName || ''}`.trim() || rep?.email
                ];
                if (!columns.some(val => (val || '').toString().toLowerCase().includes(searchLower))) {
                    return false;
                }
            }
            
            // Date range filter (checks opp.startDate or opp.date)
            let oppDate = null;
            if (opp.startDate) {
                oppDate = new Date(opp.startDate);
            } else if (opp.date) {
                oppDate = new Date(opp.date);
            }
            if (oppDate && (oppStart || oppEnd)) {
                if (oppStart && oppDate < oppStart) return false;
                if (oppEnd && oppDate > oppEnd) return false;
            }
            return true;
        });
    }, [opportunities, rep, oppSearch, oppStart, oppEnd]);

    const sortedOpportunities = useMemo(() => {
        const sorted = [...filteredOpportunities];
        sorted.sort((a, b) => {
            const col = oppSort.column;
            let valA, valB;
            if (col === 'stage') {
                valA = a.stage || '';
                valB = b.stage || '';
            } else if (col === 'customer') {
                valA = a.customerName || a.customerName || '';
                valB = b.customerName || b.customerName || '';
            } else if (col === 'leadSource') {
                valA = a.leadSource || '';
                valB = b.leadSource || '';
            } else if (col === 'value') {
                valA = Number(a.value) || 0;
                valB = Number(b.value) || 0;
            } else if (col === 'startDate') {
                valA = new Date(a.startDate || a.date || 0).getTime();
                valB = new Date(b.startDate || b.date || 0).getTime();
            } else if (col === 'dueDate') {
                valA = new Date(a.dueDate || 0).getTime();
                valB = new Date(b.dueDate || 0).getTime();
            } else {
                valA = (a[col] ?? '').toString();
                valB = (b[col] ?? '').toString();
            }
            if (typeof valA === 'string' && typeof valB === 'string') {
                return oppSort.asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return oppSort.asc ? valA - valB : valB - valA;
        });
        return sorted;
    }, [filteredOpportunities, oppSort]);

    // Reset opportunity page on visible-data changes
    useEffect(() => { 
        setOppPage(1); 
    }, [oppSearch, oppRange, oppCustomStart, oppCustomEnd]); // Removed filteredOpportunities.length dependency

    // --- Opportunities Pagination ---
    const totalOppPages = useMemo(() => 
        Math.ceil(sortedOpportunities.length / OPP_PAGE_SIZE),
        [sortedOpportunities.length]
    );
    
    const paginatedOpportunities = useMemo(() => 
        sortedOpportunities.slice(
            (oppPage - 1) * OPP_PAGE_SIZE,
            oppPage * OPP_PAGE_SIZE
        ),
        [sortedOpportunities, oppPage]
    );

    const handleAssignTask = useCallback(async (taskData) => {
        try {
            const seq = await allocateTaskNumber();

            // Generate task ID using TSK-# format (matches TaskPage.jsx)
            const taskDocId = generateTaskId(seq);
            const taskNumberLabel = taskDocId; // Use same format for label

            await setDoc(doc(db, collectionPath('tasks'), taskDocId), {
                ...taskData,
                taskNumber: seq,
                taskNumberLabel,
                uid: uid, // ensure the task is assigned to this rep
                status: 'Open',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                notes: [],
            });
            // Log the creation
            logEntityChanges({
                oldObj: null,
                newObj: { ...taskData, taskNumber: seq, taskNumberLabel },
                fields: ['title', 'status', 'priority', 'uid', 'dueDate', 'customerId', 'opportunityId', 'description'],
                entityType: 'Task',
                entityId: taskDocId,
                user: currentUser,
                action: 'Created',
            }).catch(e => console.warn('Failed to log task creation:', e));
            setShowTaskModal(false);
            setToast && setToast({ type: 'success', message: `Task ${taskNumberLabel} assigned successfully!` });
        } catch (error) {
            setToast && setToast({ type: 'error', message: 'Failed to assign task.' });
            console.error('Error adding task:', error);
        }
    }, [uid, setToast]);

    // Save handler for modal - memoized
    const handleEditModalClose = useCallback((updated, newLog) => {
        setShowEditModal(false);
        if (updated && typeof updated === 'object') {
            setRep(updated);
        }
        setLogPage(1); // Optionally reset to first page
    }, []);

    // When fetching users for dropdowns or lookups, always use uid
    // If you fetch users, use:
    useEffect(() => {
        if (!currentUser) return;
        if (can(FEATURE_PERMISSIONS.USER_VIEW_ALL)) {
            const unsub = onSnapshot(collection(db, collectionPath('users')), (snap) => {
                setUsers(snap.docs.map(doc => ({ uid: doc.id, ...doc.data() })));
            });
            return unsub;
        } else {
            getDoc(doc(db, collectionPath('users'), currentUser.uid)).then(snap => {
                if (snap.exists()) setUsers([{ uid: snap.id, ...snap.data() }]);
                else setUsers([]);
            });
        }
    }, [currentUser, can]);

    useEffect(() => {
        if (!uid || !rep) return;
        const q = query(
            collection(db, collectionPath('logsUser')), 
            orderBy('timestamp', 'desc'), 
            limit(100) // Increased from USERS_PAGE_SIZE for better coverage
        );
        const unsub = onSnapshot(q, snap => {
            const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // Robust filter: match entityId, UserId, or fallback to string match
            const filtered = all.filter(l => {
                // Show logs where this user was the actor OR the entity
                return (
                    l.userId === uid ||
                    l.userEmail === rep?.email ||
                    l.entityId === uid ||
                    (l.entityType === 'User' && l.entityId === uid)
                );
            });
            setLogs(filtered);
        }, err => {
            console.error('Failed to load logs', err);
            setLogs([]);
        });
        return unsub;
    }, [uid, rep?.email]); // Add rep?.email as dependency

    const filteredActivityLogs = useMemo(() => {
        const q = (activitySearch || '').toLowerCase().trim();
        if (!q) return logs;
        return logs.filter(log =>
            (log.action || '').toLowerCase().includes(q) ||
            (log.details || '').toLowerCase().includes(q) ||
            (log.userEmail || '').toLowerCase().includes(q) ||
            (log.timestamp && log.timestamp.toDate && log.timestamp.toDate().toLocaleString().toLowerCase().includes(q))
        );
    }, [logs, activitySearch]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!rep) {
        return <div className="p-6">Sales Rep not found.</div>;
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            <div className="flex justify-between items-center mb-6">
                <button
                    className="text-indigo-600 hover:underline"
                    onClick={() => navigate(-1)}
                >
                    &larr; Back to Sales Reps
                </button>
            </div>
            <div className="bg-white rounded-lg shadow p-6 mb-6 relative">
                {/* Edit button in top right of the card, only for admin */}
                {(can(FEATURE_PERMISSIONS.USER_EDIT) || currentUser?.uid === uid) && (
                    <button
                        className="absolute top-4 right-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500"
                        onClick={() => setShowEditModal(true)}
                    >
                        Edit
                    </button>
                )}
                <h2 className="text-2xl font-bold mb-2">
                    {(rep.firstName || rep.lastName) 
                        ? `${rep.firstName || ''} ${rep.lastName || ''}`.trim() 
                        : (rep.displayName || rep.name || rep.email || '-')
                    }
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left side */}
                    <div>
                        <div>
                            <span className="font-semibold">First Name:</span> {rep.firstName || '-'}
                        </div>
                        <div>
                            <span className="font-semibold">Last Name:</span> {rep.lastName || '-'}
                        </div>
                        <div>
                            <span className="font-semibold">Full Name:</span> {(rep.firstName || rep.lastName) ? `${rep.firstName || ''} ${rep.lastName || ''}`.trim() : (rep.displayName || rep.name || '-')}
                        </div>
                        <div>
                            <span className="font-semibold">Email:</span> {rep.email || '-'}
                        </div>
                        <div>
                            <span className="font-semibold">Phone:</span> {rep.phone || '-'}
                        </div>
                    </div>
                    {/* Right side */}
                    <div>
                        <div>
                            <span className="font-semibold">State:</span> {rep.state || '-'}
                        </div>
                        <div>
                            <span className="font-semibold">Assigned Manager:</span> {rep.managerName || '-'}
                        </div>
                        <div>
                            <span className="font-semibold">Title:</span> {rep.title || '-'}
                        </div>
                        <div>
                            <span className="font-semibold">Department:</span> {rep.department || '-'}
                        </div>
                        <div>
                            <span className="font-semibold">Territories:</span>{' '}
                            {Array.isArray(rep.territories) && rep.territories.length > 0 ? (
                                <span className="inline-flex flex-wrap gap-1 align-middle">
                                    {rep.territories.map(t => (
                                        <span
                                            key={t}
                                            className="inline-flex items-center bg-indigo-50 text-indigo-800 border border-indigo-200 text-xs rounded-full px-2 py-0.5"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </span>
                            ) : (
                                '-'
                            )}
                        </div>
                        {currentUser?.role === 'admin' && (
                            <div>
                                <span className="font-semibold">Role:</span> {rep.role ? rep.role.charAt(0).toUpperCase() + rep.role.slice(1) : '-'}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('tasks')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'tasks'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Assigned Tasks ({totalTaskCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('opportunities')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'opportunities'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Assigned Opportunities ({opportunities.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'activity'
                                ? 'border-indigo-500 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Activity Log
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow">
                {activeTab === 'tasks' && (
                    <div className="py-2 px-4">
                        {/* Pass search and pagination props if supported */}
                        <TaskPage
                            user={currentUser}
                            showToast={setToast}
                            querySalesRepId={uid}
                            page={taskPage}
                            pageSize={TASK_PAGE_SIZE}
                            search={taskSearch}
                            onTotalPages={setTotalTaskPages}
                            onTotalCount={setTotalTaskCount}
                        />
                        {/* Pagination controls for tasks */}
                        {taskSearch === '' && (
                            <PaginationControls currentPage={taskPage} totalPages={totalTaskPages} onPageChange={setTaskPage} />
                        )}
                    </div>
                )}
                {activeTab === 'opportunities' && (
                    <div className="p-6">
                        {/* Search and Date Range */}
                        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-4 space-y-2 md:space-y-0">
                            <div className="flex items-center w-full md:w-auto">
                                <span className="mr-2 font-medium text-gray-700">Search:</span>
                                <input
                                    type="text"
                                    placeholder="Search by customer, lead source, etc..."
                                    className={`border rounded px-3 py-2 w-full md:w-[20rem] transition-colors duration-150 ${
                                        oppSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                    }`}
                                    value={oppSearch}
                                    onChange={e => setOppSearch(e.target.value)}
                                />
                            </div>
                            <DateRangeSelector
                                range={oppRange}
                                setRange={setOppRange}
                                customStart={oppCustomStart}
                                setCustomStart={setOppCustomStart}
                                customEnd={oppCustomEnd}
                                setCustomEnd={setOppCustomEnd}
                                requireApply={true}
                                defaultRange="90"
                                onClear={() => setOppSearch('')}
                            />
                        </div>
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3">Opportunity #</th>
                                    <th className="px-4 py-3">Stage</th>
                                    <th className="px-4 py-3">Customer</th>
                                    <th className="px-4 py-3">Lead Source</th>
                                    <th className="px-4 py-3">Value</th>
                                    <th className="px-4 py-3">Start Date</th>
                                    <th className="px-4 py-3">Due Date</th>
                                    <th className="px-4 py-3">Sales Rep</th>
                                    <th className="px-4 py-3">Win Probability (%)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedOpportunities.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-4 text-gray-500">
                                            No opportunities assigned.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedOpportunities.map(opp => {
                                        const assignedRep =
                                            opp.salesRepName ||
                                            (opp.uid === uid
                                                ? (rep.name || `${rep.firstName || ''} ${rep.lastName || ''}` || rep.email)
                                                : opp.uid || '-');
                                        // Calculate win probability (reuse from OppPage)
                                        return (
                                            <tr key={opp.id} className="text-gray-700 hover:bg-gray-50">
                                                {/* Opportunity Number (clickable) */}
                                                <td className="px-4 py-3 text-sm font-mono">
                                                    <Link
                                                        to={`/opportunity/${opp.id}`}
                                                        className="text-indigo-600 hover:underline"
                                                    >
                                                        {opp.id || '-'}
                                                    </Link>
                                                </td>
                                                {/* Stage with color formatting */}
                                                <td className="px-4 py-3 text-sm">
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                        opp.stage === 'New' ? 'bg-green-100 text-green-700' :
                                                        opp.stage === 'Proposal Sent' ? 'bg-gray-100 text-yellow-700' :
                                                        opp.stage === 'Qualified' ? 'bg-gray-100 text-purple-700' :
                                                        opp.stage === 'Closed Won' ? 'bg-gray-100 text-green-700' :
                                                        opp.stage === 'Closed Lost' ? 'bg-gray-100 text-red-700' :
                                                        'bg-blue-100 text-blue-700'
                                                    }`}>
                                                        {opp.stage}
                                                    </span>
                                                </td>
                                                {/* Customer */}
                                                <td className="px-4 py-3 text-sm">
                                                    {opp.customerName || opp.customerName || '-'}
                                                </td>
                                                {/* Lead Source */}
                                                <td className="px-4 py-3 text-sm">
                                                    {opp.leadSource || '-'}
                                                </td>
                                                {/* Value */}
                                                <td className="px-4 py-3 text-sm font-medium">
                                                    {opp.value != null && opp.value !== '' && !isNaN(Number(opp.value)) ? `$${Number(opp.value).toLocaleString()}` : '-'}
                                                </td>
                                                {/* Start Date */}
                                                <td className="px-4 py-3 text-sm">
                                                    {opp.startDate ? (opp.startDate.toDate ? formatDate(opp.startDate.toDate(), currentUser?.dateFormat) : formatDate(new Date(opp.startDate), currentUser?.dateFormat)) : (opp.date ? (opp.date.toDate ? formatDate(opp.date.toDate(), currentUser?.dateFormat) : formatDate(new Date(opp.date), currentUser?.dateFormat)) : '-')}
                                                </td>
                                                {/* Due Date */}
                                                <td className="px-4 py-3 text-sm">
                                                    {opp.dueDate ? (opp.dueDate.toDate ? formatDate(opp.dueDate.toDate(), currentUser?.dateFormat) : formatDate(new Date(opp.dueDate), currentUser?.dateFormat)) : '-'}
                                                </td>
                                                {/* Sales Rep */}
                                                <td className="px-4 py-3 text-sm">
                                                    {assignedRep}
                                                </td>
                                                {/* Win Probability */}
                                                <td className="px-4 py-3 text-sm font-semibold">
                                                    {calculateWinProbability(opp.stage, opp.engagement)}%
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                        {/* Pagination controls */}
                        <PaginationControls currentPage={oppPage} totalPages={totalOppPages} onPageChange={setOppPage} />
                    </div>
                )}
                {activeTab === 'activity' && (
                    <div className="overflow-x-auto">
                        <div className="flex items-center mb-3">
                            <span className="mr-2 font-medium text-gray-700">Search:</span>
                            <input
                                type="text"
                                className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
                                    activitySearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                }`}
                                placeholder="Search activity log..."
                                value={activitySearch}
                                onChange={e => setActivitySearch(e.target.value)}
                            />
                        </div>
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3 whitespace-nowrap">Date</th>
                                    <th className="px-4 py-3 whitespace-nowrap">Time</th>
                                    <th className="px-4 py-3 whitespace-nowrap">User</th>
                                    <th className="px-4 py-3 whitespace-nowrap">Action</th>
                                    <th className="px-4 py-3">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredActivityLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-4 text-gray-500">No activity found.</td>
                                    </tr>
                                ) : (
                                    filteredActivityLogs.map(log => {
                                        const dateObj = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                                        return (
                                            <tr key={log.id} className="text-gray-700 hover:bg-gray-50">
                                                <td className="px-4 py-3 whitespace-nowrap">{formatDate(log.timestamp, currentUser?.dateFormat)}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">{dateObj.toLocaleTimeString()}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                  {(() => {
                                                    const u = users.find(u => u.uid === log.userId || u.email === log.userEmail);
                                                    if (!u) return log.userName || log.displayName || (log.firstName && log.lastName && `${log.firstName} ${log.lastName}`) || log.userEmail || log.userId || '-';
                                                    if (u.firstName || u.lastName) return `${u.firstName || ''} ${u.lastName || ''}`.trim();
                                                    return u.displayName || u.name || u.email || log.userEmail || log.userId || '-';
                                                })()}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">{log.action || '-'}</td>
                                                <td className="px-4 py-3">{log.details || '-'}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Task Assignment Modal */}
            {showTaskModal && (
                <TaskModal
                    onClose={() => setShowTaskModal(false)}
                    onSubmit={handleAssignTask}
                    initialValues={{}}
                    uid={uid}
                />
            )}
            {/* Sales Rep Edit Modal */}
            {showEditModal && rep && (
                <UserModal
                    user={currentUser}
                    open={true}
                    onClose={handleEditModalClose} // <-- Use your handler that updates rep
                    initial={rep}

                />
            )}
        </main>
    );
};

export default SalesRepDetailPage;




